/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import SkillStats from "model/stats/SkillStats";
import CharacterService from "./CharacterService";
import { ClassDetails } from "model/character/ClassDetails";
import { getSecondClassSkillProficiencies } from "./ClassMetadata";

interface Option {
    label: string,
    options: string[]
};

export function parseSkills(classDetails: ClassDetails, skillOptions: Option[],
                            skillsSelected: string[], isPrimaryClass: boolean): void {
    /*
    "Choose any three",
    "Choose four from Acrobatics, Athletics, Deception, Insight, Intimidation, Investigation, Perception, Performance, Persuasion, Sleight of Hand, and Stealth",
    "Choose three from Animal Handling, Athletics, Insight, Investigation, Nature, Perception, Stealth, and Survival",
    "Choose two from Acrobatics, Athletics, History, Insight, Religion, and Stealth",
    "Choose two from Animal Handling, Athletics, Intimidation, Nature, Perception, and Survival",
    "Choose two from Arcana, Animal Handling, Insight, Medicine, Nature, Perception, Religion, and Survival",
    "Choose two from Arcana, Deception, Insight, Intimidation, Persuasion, and Religion",
    "Choose two from Arcana, History, Insight, Investigation, Medicine, and Religion",
    "Choose two from Athletics, Insight, Intimidation, Medicine, Persuasion, and Religion",
    "Choose two from History, Insight, Medicine, Persuasion, and Religion",
    "Choose two skills from Acrobatics, Animal, Handling, Athletics, History, Insight, Intimidation, Perception, and Survival",
    "Choose two skills from Arcana, Deception, History, Intimidation, Investigation, Nature, and Religion",
    */
    if (!classDetails.classInfo) {
        return;
    }

    if (isPrimaryClass) {
        CharacterService.splitSkills(classDetails.classInfo.prof_skills ?? '', classDetails.classInfo.name ?? '', skillOptions, skillsSelected);
    }
    // Go through the sections for the class and archetype, and parse them for skill proficiencies:
    if (classDetails.classLevel < 3) {
        classDetails.archetype = null;
    }
    for (const classInst of [classDetails.classInfo, classDetails.archetype].filter(o => o != null)) {
        for (const section of classInst?.sections || []) {
            if (section.minLevel && section.minLevel > classDetails.classLevel) {
                continue;
            }
            const newOptions = []; // an array of arrays of strings
            let fullyMatched = true;
            if (section.desc === 'When you join the College of Lore at 3rd level, you gain proficiency with three skills of your choice.') {
                newOptions.push([...SkillStats.getSkillsMap().keys()]);
                newOptions.push([...SkillStats.getSkillsMap().keys()]);
                newOptions.push([...SkillStats.getSkillsMap().keys()]);
            } else if (section.desc === 'When you join the College of Shadows at 3rd level, you gain proficiency in Stealth and in two other skills of your choice.') {
                newOptions.push(['Stealth']);
                newOptions.push([...SkillStats.getSkillsMap().keys()]);
                newOptions.push([...SkillStats.getSkillsMap().keys()]);
            } else if (section.desc === 'When you join the College of Investigation at 3rd level, you gain proficiency in the Insight skill and in two of' +
                                        ' the following skills of your choice: Acrobatics, Deception, Investigation, Performance, Sleight of Hand, or Stealth.') {
                newOptions.push(['Insight']);
                newOptions.push(['Acrobatics', 'Deception', 'Investigation', 'Performance', 'Sleight of Hand', 'Stealth']);
                newOptions.push(['Acrobatics', 'Deception', 'Investigation', 'Performance', 'Sleight of Hand', 'Stealth']);
            } else if (section.desc === 'At 3rd level, you gain proficiency in the Stealth skill. If you are already proficient in Stealth,' +
                                        ' you gain proficiency in another barbarian class skill of your choice.') {
                let hasStealth = false;
                for (const skillSelected of skillsSelected) {
                    if (skillSelected === 'Stealth') {
                        hasStealth = true;
                        break;
                    }
                }
                if (!hasStealth) {
                    newOptions.push(['Stealth']);
                } else {
                    // barbarian class skills are: Animal Handling, Athletics, Intimidation, Nature, Perception, and Survival
                    newOptions.push(['Stealth', 'Animal Handling', 'Athletics', 'Intimidation', 'Nature', 'Perception', 'Survival']);
                }
            } else if (section.desc === 'When you adopt this path at 3rd level, you gain proficiency in History and Performance.' +
                                        ' If you already have proficiency in one of these skills, your proficiency bonus is doubled for ability checks you make using that skill.') {
                newOptions.push(['History', 'History' + SkillStats.EXPERT]);
                newOptions.push(['Performance', 'Performance' + SkillStats.EXPERT]);
                fullyMatched = false;
            } else if (section.desc === 'When you join the College of the Cat at 3rd level, you gain proficiency with the Acrobatics and Stealth skills and with thieves\' tools' +
                                        ' if you don\'t already have them. In addition, if you\'re proficient with a simple or martial melee weapon,' +
                                        ' you can use it as a spellcasting focus for your bard spells.') {
                fullyMatched = false;
                newOptions.push(['Acrobatics']);
                newOptions.push(['Stealth']);
                newOptions.push(['Thieves\' tools']);
            } else if (section.desc === 'When you choose this domain at 1st level, you take your place on the line between the two aspects of mercy: healing and killing.' +
                                        ' You gain proficiency in the Medicine skill and with the poisoner\'s kit. In addition, you gain proficiency with heavy armor and martial weapons.') {
                fullyMatched = false;
                newOptions.push(['Medicine']);
                newOptions.push(['Poisoner\'s kit']);
            } else if (section.desc === 'When you choose this domain at 1st level, you gain proficiency with heavy armor and either cartographer\'s tools or navigator\'s tools' +
                                        ' (your choice). In addition, you gain proficiency in the Arcana skill.') {
                fullyMatched = false;
                newOptions.push(['Arcana']);
                newOptions.push(['Cartographer\'s tools']);
                newOptions.push(['Navigator\'s tools']);
            } else if (section.desc === 'When you choose this domain at 1st level, you learn the *poison spray* cantrip. In addition, you gain proficiency in the Deception skill,' +
                                        ' with a poisoner\'s kit, and with martial weapons that have the Finesse property.' +
                                        ' You can apply poison to a melee weapon or three pieces of ammunition as a bonus action.') {
                fullyMatched = false;
                newOptions.push(['Deception']);
                newOptions.push(['Poisoner\'s kit']);
            } else if (section.desc === 'When you choose this domain at 1st level, you gain proficiency in the Stealth skill and darkvision out to a range of 60 feet.' +
                                        ' If you already have darkvision, its range increases by 30 feet. In addition, when you are in dim light or darkness,' +
                                        ' you can use a bonus action to Hide.') {
                fullyMatched = false;
                newOptions.push(['Stealth']);
            } else if (section.desc === 'When you choose this domain at 1st level, you learn the *mage hand* cantrip and gain proficiency in the Nature skill.' +
                                        ' When you cast *mage hand*, you can make the hand invisible, and you can control the hand as a bonus action.') {
                fullyMatched = false;
                newOptions.push(['Nature']);
            } else if (section.desc.startsWith('When you choose this circle at 2nd level, you gain proficiency in the Acrobatics or Stealth skill (your choice),' +
                                               ' and you can speak and understand Bee Dance, a language shared by bees that involves flying in dance-like patterns.' +
                                               ' Bees refuse to attack you, even with magical coercion.')) {
                fullyMatched = false;
                newOptions.push(['Acrobatics', 'Stealth']);
            } else if (section.desc === 'When you choose this archetype at 3rd level, you gain proficiency in the Insight, Medicine, or Religion skill (your choice).') {
                newOptions.push(['Insight', 'Medicine', 'Religion']);
            } else if (section.desc === 'When you choose this tradition at 3rd level, you gain proficiency in the Acrobatics skill if you don\'t already have it.' +
                                        ' When you move at least 10 feet on your turn, you have advantage on the next Dexterity (Acrobatics) check you make before the start of your next turn.') {
                fullyMatched = false;
                newOptions.push(['Acrobatics']);
            } else if (section.desc === 'Starting at 3rd level, you gain proficiency in Animal Handling. If you already have proficiency in this skill,' +
                                        ' your proficiency bonus is doubled for any ability check you make with it.') {
                fullyMatched = false;
                newOptions.push(['Animal Handling', 'Animal Handling' + SkillStats.EXPERT]);
            } else if (section.desc === 'When you choose this archetype at 3rd level, you gain proficiency in the Religion skill, if you don-t already have it.') {
                newOptions.push(['Religion']);
            } else if (section.desc === 'Starting at 1st level, you learn the *druidcraft* cantrip, and you gain proficiency in the Animal Handling skill.') {
                fullyMatched = false;
                newOptions.push(['Animal Handling']);
            } else if (section.desc === 'At 2nd level, you gain proficiency with longbows and shortbows, and you gain proficiency in the Stealth skill.' +
                                        ' In addition, you can still perform the somatic components of wizard spells even when you have a longbow or shortbow in one or both hands.') {
                fullyMatched = false;
                newOptions.push(['Stealth']);
            } else if (section.desc === 'At 2nd level, you can use an action to inscribe a small rune on a corpse. While this rune remains, the corpse can\'t become undead.' +
                                        ' You can use this feature a number of times equal to your Intelligence modifier (a minimum of once).' +
                                        ' You regain all expended uses when you finish a long rest.\n  In addition, you have proficiency in the Religion skill if you don\'t already have it,' +
                                        ' and you have advantage on Intelligence (Religion) checks made to recall lore about deities of death, burial practices, and the afterlife.') {
                fullyMatched = false;
                newOptions.push(['Religion']);
            }
            // expertise checks:
            else if (section.desc === 'At 3rd level, choose two of your skill proficiencies.' +
                                      ' Your proficiency bonus is doubled for any ability check you make that uses either of the chosen proficiencies.' +
                                      '\nAt 10th level, you can choose another two skill proficiencies to gain this benefit.') {
                if (classDetails.classLevel > 2) {
                    newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                    newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                    if (classDetails.classLevel > 9) {
                        newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                        newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                    }
                }
            } else if (section.desc === 'At 1st level, choose two of your skill proficiencies, or one of your skill proficiencies and' +
                                        ' your proficiency with thieves\' tools. Your proficiency bonus is doubled for any ability check you make that uses either of the chosen proficiencies.' +
                                        '\nAt 6th level, you can choose two more of your proficiencies (in skills or with thieves\' tools) to gain this benefit.') {
                newOptions.push([...SkillStats.getSkillsMap().keys(), 'Thieves\' tools'].map(o => o + SkillStats.EXPERT));
                newOptions.push([...SkillStats.getSkillsMap().keys(), 'Thieves\' tools'].map(o => o + SkillStats.EXPERT));
                if (classDetails.classLevel > 5) {
                    newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                    newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                }
            } else {
                continue;
            }

            if (fullyMatched) {
                section.exportSection = false;
            }
            for (const options of newOptions) {
                skillOptions.push({ label: (classInst?.name ?? '') + ' - ' + section.name, options });
                if (options.length === 1) {
                    skillsSelected.push(options[0]);
                } else {
                    skillsSelected.push('');
                }
            }

            // cantrips:
            'At 1st level, your patron gifts you with the skills necessary to defend it. You gain proficiency with shields, and you learn the *shillelagh* cantrip. *Shillelagh* counts as a warlock cantrip for you, but it doesn\'\'t count against your number of cantrips known.'
        }
    }

    /*
        "Choose one type of artisan's tools or one musical instrument",
        "Herbalism kit",
        "None",
        "Thieves' tools",
        "Three musical instruments of your choice"
    */
    if (isPrimaryClass) {
        const tools = classDetails.classInfo.prof_tools;
        if (!tools || tools === 'None') {
            return;
        }
        if (tools === 'Choose one type of artisan\'s tools or one musical instrument') {
            skillOptions.push({ label: classDetails.classInfo.name + ' (tool/inst)', options: [...SkillStats.tools, ...SkillStats.musicalInstruments] });
            skillsSelected.push('');
            return;
        }
        if (tools === 'Three musical instruments of your choice') {
            for (let i = 0; i < 3; i++) {
                skillOptions.push({ label: classDetails.classInfo.name + ' (instr)', options: [...SkillStats.musicalInstruments] });
                skillsSelected.push('');
            }
        }
        if (tools === 'Herbalism kit') {
            skillOptions.push({ label: classDetails.classInfo.name + ' (tool)', options: ['Herbalist kit'] });
            skillsSelected.push('Herbalist kit');
        }
        if (tools === 'Thieves\' tools') {
            skillOptions.push({ label: classDetails.classInfo.name + ' (tool)', options: ['Thieves\' tools'] });
            skillsSelected.push('Thieves\' tools');
        }
        for (const opt of [...SkillStats.tools, ...SkillStats.musicalInstruments]) {
            if (opt.toLowerCase() == tools) {
                skillOptions.push({ label: classDetails.classInfo.name, options: [opt] });
                skillsSelected.push(opt);
                break;
            }
        }
    } else {
        const secondClassProficiencies = getSecondClassSkillProficiencies(classDetails.classInfo);
        if (secondClassProficiencies?.includes('One skill of your choice')) {
            skillOptions.push({ label: classDetails.classInfo.name, options: [...SkillStats.getSkillsMap().keys()] });
            skillsSelected.push('');
        }
        if (secondClassProficiencies?.includes('one Musical Instrument of your choice')) {
            skillOptions.push({ label: classDetails.classInfo.name + ' (inst)', options: [...SkillStats.musicalInstruments] });
            skillsSelected.push('');
        }
        if (secondClassProficiencies === 'Choose one from Animal Handling, Athletics, Insight, Investigation, Nature, Perception, Stealth, and Survival') {
            skillOptions.push({ label: classDetails.classInfo.name, options: ['Animal Handling', 'Athletics', 'Insight', 'Investigation', 'Nature', 'Perception', 'Stealth', 'Survival'] });
            skillsSelected.push('');
        }
        if (secondClassProficiencies === 'Thieves\' Tools, plus choose one from Acrobatics, Athletics, Deception, Insight, Intimidation, Investigation, Perception, Performance, Persuasion, Sleight of Hand, and Stealth') {
            skillOptions.push({ label: classDetails.classInfo.name, options: ['Thieves\' tools'] });
            skillsSelected.push('Thieves\' Tools');
            skillOptions.push({ label: classDetails.classInfo.name, options: ['Acrobatics', 'Athletics', 'Deception', 'Insight', 'Intimidation', 'Investigation', 'Perception', 'Performance', 'Persuasion', 'Sleight of Hand', 'Stealth'] });
            skillsSelected.push('');
        }
        //           if (secondClassProficiencies.includes('thieves\' tools')) {
        //               skillOptions.push({ label: classInfo.name, options: ['Thieves\' tools'] });
        //               skillsSelected.push('Thieves\' tools');
        //           }
    }
}
